import type { AuditFindingPartial, UserAuditRule } from "./types";
import { addressFromRC } from "./addressing";
import type { SheetModel, WorkbookModel } from "../../model";
import { Origin, ValueKind } from "../../model";

/**
 * Evaluate a user-defined audit rule against one sheet's substrate
 * data. Returns findings for cells that match all specified criteria.
 *
 * Substrate-native — reads cellOrigin, cellValueKind, numericGrid,
 * textValueIds + textValueStrings, formulaA1Ids + formulaA1Strings.
 *
 * Supported criteria:
 *   - cellHasError    → cellValueKind === ERROR
 *   - textRegex       → applied to string-coerced cell value
 *   - formulaRegex    → applied to A1 formula (formula cells only)
 *   - fillColor       → format properties aren't captured by the
 *   - fontColor          substrate, so rules using any of these
 *   - bold               narrow to zero matches. Matches the legacy
 *   - italic             matchEvaluator's behavior, which silently
 *                        skipped them too (the engine didn't load
 *                        formatting). Will become real if/when the
 *                        substrate gains format-property support.
 *
 * Known lossiness vs legacy `String(value)` semantics:
 *   - BOOLEAN cells stringify as "false" sentinel (substrate doesn't
 *     track the actual true/false). Legacy stringified as "true" /
 *     "false" from Office.js.
 *   - ERROR cells stringify as "#REF!" sentinel (substrate doesn't
 *     intern the actual error literal). Legacy stringified the
 *     specific "#N/A" / "#DIV/0!" / etc. the user typed.
 *   Both edges affect textRegex on rules that care about boolean or
 *   error stringification specifically.
 */
export function evaluateUserAuditRule(
  rule: UserAuditRule,
  sheet: SheetModel,
  model: WorkbookModel
): AuditFindingPartial[] {
  const findings: AuditFindingPartial[] = [];
  const m = rule.match;

  let textRe: RegExp | null = null;
  let formulaRe: RegExp | null = null;
  try {
    if (m.textRegex) textRe = new RegExp(m.textRegex.pattern, m.textRegex.flags);
    if (m.formulaRegex) formulaRe = new RegExp(m.formulaRegex.pattern, m.formulaRegex.flags);
  } catch {
    // Invalid regex — return no findings rather than throwing
    return [];
  }

  if (!hasAnyCriteria(rule)) return [];

  // If any format-property matcher is set, the rule never matches —
  // substrate doesn't capture them. Skip the whole sheet walk.
  if (
    m.fillColor !== undefined ||
    m.fontColor !== undefined ||
    m.bold !== undefined ||
    m.italic !== undefined
  ) {
    return [];
  }

  const { rows, cols, startRow, startCol } = sheet.bounds;
  const cellOrigin = sheet.cellOrigin;
  const cellValueKind = sheet.cellValueKind;
  const numericGrid = sheet.numericGrid;
  const textValueIds = sheet.textValueIds;
  const formulaA1Ids = sheet.formulaA1Ids;
  const textValueStrings = model.catalogs.textValueStrings;
  const formulaA1Strings = model.catalogs.formulaA1Strings;

  for (let r = 0; r < rows; r++) {
    const rowBase = r * cols;
    for (let c = 0; c < cols; c++) {
      const idx = rowBase + c;
      const vk = cellValueKind[idx];
      const origin = cellOrigin[idx];

      if (m.cellHasError === true && vk !== ValueKind.ERROR) continue;
      if (m.cellHasError === false && vk === ValueKind.ERROR) continue;

      if (textRe) {
        const text = stringifyCellValue(vk, idx, numericGrid, textValueIds, textValueStrings);
        if (!textRe.test(text)) continue;
      }

      if (formulaRe) {
        if (origin !== Origin.FORMULA) continue;
        const fid = formulaA1Ids[idx];
        if (fid < 0) continue;
        if (!formulaRe.test(formulaA1Strings[fid])) continue;
      }

      findings.push({
        sheetName: sheet.name,
        address: addressFromRC(startRow + r, startCol + c),
        message: rule.message,
      });
    }
  }

  return findings;
}

function hasAnyCriteria(rule: UserAuditRule): boolean {
  const m = rule.match;
  return (
    m.cellHasError !== undefined ||
    m.fillColor !== undefined ||
    m.fontColor !== undefined ||
    m.bold !== undefined ||
    m.italic !== undefined ||
    m.textRegex !== undefined ||
    m.formulaRegex !== undefined
  );
}

function stringifyCellValue(
  vk: number,
  idx: number,
  numericGrid: Float64Array,
  textValueIds: Int32Array,
  textValueStrings: string[]
): string {
  switch (vk) {
    case ValueKind.NUMBER:
    case ValueKind.DATE:
      return String(numericGrid[idx]);
    case ValueKind.TEXT: {
      const tid = textValueIds[idx];
      return tid >= 0 ? textValueStrings[tid] : "";
    }
    case ValueKind.BOOLEAN:
      return "false"; // sentinel — substrate doesn't track actual value
    case ValueKind.ERROR:
      return "#REF!"; // sentinel — substrate doesn't intern actual literal
    default:
      return "";
  }
}
